import { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShoppingBag, ShoppingCart, MessageCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useSettings } from '../context/SettingsContext';
import { ShopCheckoutModal } from '../components/ShopCheckoutModal';
import { PriceTag } from '../components/PriceTag';
import { ExpandableText } from '../components/ExpandableText';

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const { settings } = useSettings();

  useEffect(() => {
    api.get('/products').then(({ data }) => setProducts(data || [])).catch(() => setProducts([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-24 pb-20">
      <section className="relative">
        <div className="absolute inset-0 hero-radial pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#00b4d8] font-semibold mb-3">Shop</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl">Dive gear, hand-picked.</h1>
          <p className="mt-5 max-w-2xl text-white/75 text-base sm:text-lg leading-relaxed">Computers, masks, fins, BCDs, regulators and Arab Diver Tours merchandise. Carefully selected by our instructors and trusted on every dive we run.</p>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-80 rounded-3xl bg-white/5 border border-white/10 animate-pulse" />
              ))}
            </div>
          )}
          {!loading && products.length === 0 && (
            <div data-testid="shop-empty-state" className="rounded-3xl border border-white/10 bg-white/5 p-12 sm:p-16 text-center">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-[#c8e600]/15 text-[#c8e600] inline-flex items-center justify-center mb-5"><ShoppingBag className="h-6 w-6" /></div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold">Coming soon</h2>
              <p className="mt-3 text-white/60 max-w-md mx-auto">Our shop is being stocked. Get in touch and we will help you order anything you need today.</p>
              {settings?.whatsapp_url && (
                <a href={settings.whatsapp_url} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex">
                  <Button className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] rounded-xl"><MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us</Button>
                </a>
              )}
            </div>
          )}
          {!loading && products.length > 0 && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {products.map((p) => (
                <Card data-testid="shop-product-card" key={p.id} className="group rounded-3xl border-white/10 bg-white/5 overflow-hidden flex flex-col">
                  <div className="relative aspect-square overflow-hidden bg-[#0d223d]">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-white/40"><ShoppingBag className="h-10 w-10" /></div>
                    )}
                  </div>
                  <div className="p-5 flex flex-col gap-3 flex-1">
                    <div className="font-display text-lg font-semibold leading-snug">{p.name}</div>
                    {p.description && (
                      <ExpandableText
                        text={p.description}
                        limit={120}
                        threshold={20}
                        className="text-xs text-white/60 leading-relaxed whitespace-pre-line"
                        testId={`shop-product-desc-${p.id}`}
                      />
                    )}
                    <div className="flex items-center justify-between mt-auto pt-3 gap-2">
                      <PriceTag
                        price={p.price}
                        compareAtPrice={p.compare_at_price}
                        size="md"
                        data-testid={`shop-product-price-${p.id}`}
                      />
                      <Button
                        size="sm"
                        data-testid="shop-buy-now-button"
                        onClick={() => setCheckoutProduct(p)}
                        className="bg-[#00b4d8] text-[#0a1628] hover:bg-[#0098b8] font-semibold rounded-lg shrink-0"
                      >
                        <ShoppingCart className="mr-1.5 h-3.5 w-3.5" /> Buy Now
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <ShopCheckoutModal
        open={!!checkoutProduct}
        onOpenChange={(v) => { if (!v) setCheckoutProduct(null); }}
        product={checkoutProduct}
      />
    </div>
  );
}
